import { useQuery } from 'react-query';
import { UseMutationResult, useMutation } from 'react-query';

//APP
import { api } from '../../../api';
import dayjs from 'dayjs';
import { localConfig } from '../../../../Configs/localConfig';
import { errorAlert } from '../../../../utils/alert';
import { getFornecedor } from '../../../Fornecedores';
import { tiposSistema } from '../../../Afins';
import { getRepresentanteLegal, RepresentanteLegalItens } from '../../../RepresentanteLegal';
import { ClienteItensTypes, getCliente } from '../../../Cliente';
import { getLabAnalise, LabAnaliseItens } from '../../../LabAnalises';

//=================================================================================>
// BUSCAR LISTA DE ELEMENTOS
//<=================================================================================

//TYPES
type typeAnalisesLab = {
    cod_laboratorio: number,
    cod_analise: number,
    cod_categoria_fornecedor: number,
    laboratorio_nome: string,
    analise_nome: string,
    profundidade_nome: string,
    cod_profundidade: number,
    cor: string,
    data: string,
    valor: string,
    analises: { 
        profundidade: { nome: string, cod: number},
        analises: { cod: number, nome: string }[]
    }
}

type ParamsReq = {
    cod_laboratorio: number;
}
//INTERFACE
export interface InterfaceAnalisesLab {
    itens: typeAnalisesLab[],
    total: number,
    pagina: number,
    quantidadePorPagina: number
}

export const useGetAnalisesLaboratorio= ({cod_laboratorio}:ParamsReq) => {
    
    const config = localConfig()
    let data_de_hoje = dayjs().format("YYYY-MM-SS");
    
    const queryKey: any = [`laboratorio-analise_analises_${config.conta}_${cod_laboratorio}_${data_de_hoje}`];

    const { data, isFetching, refetch, isError } = useQuery<InterfaceAnalisesLab, Error>(queryKey, async () => {
        return api.get(`/laboratorio-analise/analises?codConta=${config.conta}&codLaboratorio=${cod_laboratorio}&dataVigente=${data_de_hoje}&pagination=-1`)
            .then((response):any => {
                return response.data
            })
            .catch((error) => {
                errorAlert(error?.response?.data?.error ?? 'Falha ao aobter analises do laboratório!');
            })
    },
        {
            enabled: false,
            refetchOnWindowFocus: false
        }
    );

    return {
        data_analises_lab: data?.itens ?? [],
        carregando_analises_lab: isFetching,
        refetch_analises_lab: refetch,
        isError: isError
    };
};

//=================================================================================>
// BUSCAR LABORATÓRIOS (fornecedores da categoria 2)
//<=================================================================================
export const useGetLaboratorios = () => {
    const config = localConfig();
    const queryKey: any = [`laboratorios_${config.conta}`];

    const { data, isFetching, refetch, isError } = useQuery(queryKey, async () => {
        const resp = await getFornecedor({ codConta: config.conta, codCategoria: 2 });
        return resp.data.itens;
    }, {
        refetchOnWindowFocus: false,
    });

    return {
        data_laboratorios: data ?? [],
        carregando_laboratorios: isFetching,
        refetch_laboratorios: refetch,
        isError,
    };
};

//=================================================================================>
// BUSCAR TIPOS DE ANÁLISE (tipos-sistema codTipo 281)
//<=================================================================================
const COD_TIPO_ANALISE = 281;

export const useGetTiposAnalise = () => {
    const config = localConfig();
    const queryKey: any = [`tipos_analise_${config.conta}`];

    const { data, isFetching, refetch, isError } = useQuery(queryKey, async () => {
        return tiposSistema(COD_TIPO_ANALISE);
    }, {
        refetchOnWindowFocus: false,
    });

    return {
        data_tipos_analise: data ?? [],
        carregando_tipos_analise: isFetching,
        refetch_tipos_analise: refetch,
        isError,
    };
};

//=================================================================================>
// BUSCAR REPRESENTANTES LEGAIS DO CLIENTE
//<=================================================================================
export const useGetRepresentantesLegais = () => {
    const config = localConfig();
    const queryKey: any = [`representantes_legais_${config.conta}_${config.codCliente}`];

    const { data, isFetching, refetch, isError } = useQuery<RepresentanteLegalItens[], Error>(queryKey, async () => {
        const resp = await getRepresentanteLegal('', config.codCliente);
        return resp.data.itens;
    }, {
        refetchOnWindowFocus: false,
    });

    return {
        data_representantes: data ?? [],
        carregando_representantes: isFetching,
        refetch_representantes: refetch,
        isError,
    };
};

//=================================================================================>
// BUSCAR DADOS DO CLIENTE LOGADO
//<=================================================================================
export const useGetClienteAtual = () => {
    const config = localConfig();
    const queryKey: any = [`cliente_atual_${config.conta}_${config.codCliente}`];

    const { data, isFetching, refetch, isError } = useQuery<ClienteItensTypes, Error>(queryKey, async () => {
        const resp = await getCliente(config.codCliente, '', '', '');
        return resp.data.itens?.[0];
    }, {
        enabled: config.codCliente !== '' && config.codCliente != null,
        refetchOnWindowFocus: false,
    });

    return {
        data_cliente: data,
        carregando_cliente: isFetching,
        refetch_cliente: refetch,
        isError,
    };
};

//=================================================================================>
// BUSCAR GRUPO DE ANÁLISES DE UM LABORATÓRIO (para a metade "Análises")
//<=================================================================================
export const useGetGrupoAnalises = (cod_laboratorio: number | null, data_vigencia?: string) => {
    const config = localConfig();
    const queryKey: any = [`grupo_analises_${config.conta}_${cod_laboratorio}_${data_vigencia ?? 'hoje'}`];

    const { data, isFetching, refetch, isError } = useQuery<LabAnaliseItens[], Error>(queryKey, async () => {
        const resp = await getLabAnalise('', cod_laboratorio, data_vigencia);
        return resp.data.itens;
    }, {
        enabled: !!cod_laboratorio,
        refetchOnWindowFocus: false,
    });

    return {
        data_grupo_analises: data ?? [],
        carregando_grupo_analises: isFetching,
        refetch_grupo_analises: refetch,
        isError,
    };
};